import React, {useEffect, useMemo} from "react";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {useAuthStore} from "@/Stores/AuthStore";
import styled from "styled-components";
import {snakeToNormalCase} from "@/Helpers/general";
import UserAvatar from "@/Components/Partials/UserAvatar";
import BaseTextInput from "@/Components/Forms/Form/BaseTextInput";
import PageLayout from "@/Themes/Default/Layouts/PageLayout";
import {Divider} from "primereact/divider";
import {z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useValidationErrors} from "@/Hooks/useValidationErrors";
import {useUpdateUser} from "@/Service/Api/V1/ApiHooks/Users/useUpdateUser";
import {QueryKeys} from "@/Service/Api/V1/QueryKeys/QueryKeys";
import {UpdateUserRequestRoleEnum} from "@/Stub/V1";
import {useQueryClient} from "@tanstack/react-query";
import {useToastMessagesStore} from "@/Stores/ToastMessagesStore";
import {Button} from "primereact/button";

const StyledWrap = styled.div`
    .form {
        .p-inputtext {
            width: 100%;
        }
    }
`;

const UserFormSchema = z.object({
    name: z.string({
        required_error: 'Name is required'
    }),
    password: z.string()
        .optional()
        .refine(
            (val) => !val || val.length === 0 || val.length >= 8,
            {
                message: 'Password must contain at least 8 characters (if provided).',
            }
        ),
    confirm_password: z.string()
        .optional()
        .refine(
            (val) => !val || val.length === 0 || val.length >= 8,
            {
                message: 'Password must contain at least 8 characters (if provided).',
            }
        ),
}).superRefine((data, ctx) => {
    if (data.password && !(/\d/.test(data.password))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Password must contain at least one number.',
            path: ['password']
        });
    }
    if (data.password && data.password.toLowerCase() === data.password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Password must contain at least one uppercase and one lowercase letter.',
            path: ['password']
        });
    }

    if (data.password && data.confirm_password !== data.password) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Passwords do not match',
            path: ['confirm_password'],
        });
    }
});

export type UserFormData = z.infer<typeof UserFormSchema>;

/*
 *
 * My profile page
 */
const MyProfilePage = () => {
    const authUser = useAuthStore((state) => state.authUser);
    const setAuthUser = useAuthStore((state) => state.setAuthUser);
    const {mutateAsync: updateUser, isPending} = useUpdateUser();
    const queryClient = useQueryClient();
    const addToastMessage = useToastMessagesStore((state) => state.addToastMessage);

    const formInitialValues = useMemo(() => {
        return {
            name: authUser?.name ?? '',
            password: '',
            confirm_password: ''
        };

    }, [authUser]);

    const {
        control,
        setError,
        reset,
        setValue,
        watch,
        formState: {errors},
        handleSubmit,
    } = useForm<UserFormData>({
        resolver: zodResolver(UserFormSchema),
        defaultValues: {...formInitialValues}
    });

    const handleUpdateUser = async (data: UserFormData) => {
        await updateUser({
            user_id: authUser?.id as number,
            UpdateUserRequest: {
                ...data,
                id: authUser?.id as number,
                client_id: authUser?.client?.id as number,
                team_id: authUser?.team?.id as number,
                role: authUser?.role as UpdateUserRequestRoleEnum
            }
        }, {
            onSuccess: async (data) => {
                setAuthUser(data)
                await queryClient.invalidateQueries({
                    queryKey: QueryKeys.auth._def
                });
                addToastMessage({
                    severity: 'success',
                    summary: 'Profile updated',
                    detail: 'Your profile has been updated successfully',
                });
            },
        });
    };

    useEffect(() => {
        document.title = 'My Profile' + ' · ' + document.title;
    }, []);

    return (
        <PageLayout>
            <StyledWrap>
                <form onSubmit={handleSubmit(handleUpdateUser)}>
                    <div className="form formgrid grid mb-5">
                        <div className="col-12 md:col-3 lg:col-2">
                            <UserAvatar avatar={authUser?.profile_photo_path}/>
                        </div>

                        <div className="col-12 md:col-9 lg:col-10 pl-3">
                            <div className="grid">
                                <h4 className={'col-12 mt-0 mb-1 font-bold'}>Personal info</h4>
                                <div className="field col-12 md:col-6 lg:col-6">
                                    <BaseTextInput
                                        label="Name"
                                        control={control}
                                        name={'name'}
                                        errorMessages={errors.name?.message}
                                    />
                                </div>
                                <div className="field col-12 md:col-6 lg:col-6">
                                    <BaseTextInput
                                        label="Email"
                                        value={authUser?.email}
                                        disabled
                                        readOnly
                                    />
                                </div>

                                <div className="field col-12 md:col-6 lg:col-6">
                                    <BaseTextInput
                                        control={control}
                                        tooltip={'Leave it blank if don\'t want to change user\'s password!'}
                                        tooltipOptions={{position: 'top'}}
                                        type={'password'}
                                        name={"password"}
                                        label={'Password'}
                                        disabled={!authUser?.is_non_sso_user}
                                        errorMessages={errors.password?.message}
                                    />
                                </div>
                                <div className="field col-12 md:col-6 lg:col-6">
                                    <BaseTextInput
                                        control={control}
                                        tooltip={'Leave it blank if don\'t want to change user\'s password!'}
                                        tooltipOptions={{position: 'top'}}
                                        type={'password'}
                                        name={"confirm_password"}
                                        label={'Confirm Password'}
                                        disabled={!authUser?.is_non_sso_user}
                                        errorMessages={errors.confirm_password?.message}
                                    />
                                </div>

                                <Divider/>

                                <h4 className={'col-12 mt-0 mb-1 font-bold'}>General</h4>
                                <div className="field col-12 md:col-6 lg:col-6">
                                    <BaseTextInput
                                        label="Client"
                                        value={authUser?.client?.name}
                                        disabled
                                        readOnly
                                    />
                                </div>
                                <div className="field col-12 md:col-6 lg:col-6">
                                    <BaseTextInput
                                        label="Team"
                                        value={authUser?.team?.name}
                                        disabled
                                        readOnly
                                    />
                                </div>
                                <div className="field col-12 md:col-6 lg:col-6">
                                    <BaseTextInput
                                        label="Role"
                                        value={snakeToNormalCase(authUser?.role)}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-content-end">
                        <Button
                            label={"Update"}
                            type={"submit"}
                            severity={"success"}
                            loading={isPending}
                        />
                    </div>
                </form>
            </StyledWrap>
        </PageLayout>
    );
};

export default withAuthenticationRequired(MyProfilePage);
